import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Shield, RefreshCw, Users, Building2, Tag, AlertCircle,
  CheckCircle2, Clock, Search, ChevronDown, ChevronUp
} from 'lucide-react';

const GROUP_LABELS = {
  warehouse: 'Warehouse',
  operations: 'Operations',
  sales_office: 'Sales Office',
  admin: 'Admin',
  all: 'All',
};

const ROLE_COLOURS = {
  super_admin: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  user: 'bg-gray-100 text-gray-600',
};

export const CRMSecurity = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [toggling, setToggling] = useState(null); // user_id being toggled
  const [lastSync, setLastSync] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, syncRes] = await Promise.all([
        api.get(`${API}/crm-security/users`),
        api.get(`${API}/crm-security/sync-status`),
      ]);
      setUsers(usersRes.data.users || []);
      setLastSync(syncRes.data.last_sync || null);
    } catch (err) {
      console.error('Failed to load CRM security data', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleToggle = async (userId) => {
    setToggling(userId);
    try {
      const res = await api.post(`${API}/crm-security/toggle/${userId}`);
      const { success, crm_access_enabled, message } = res.data;
      if (success) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, crm_access_enabled } : u));
        toast.success(message);
      } else {
        toast.error(message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to toggle CRM access');
    } finally {
      setToggling(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`${API}/crm-security/sync`);
      const { message, revoked } = res.data;
      toast.success(message);
      if (revoked > 0) {
        // Reload to reflect revocations
        await loadUsers();
      } else {
        // Just update the sync status
        const syncRes = await api.get(`${API}/crm-security/sync-status`);
        setLastSync(syncRes.data.last_sync || null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return (
      !q ||
      u.username?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.full_name?.toLowerCase().includes(q)
    );
  });

  const enabledCount = users.filter(u => u.crm_access_enabled).length;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Shield className="w-5 h-5 text-blue-600" />
            CRM Access Control
          </CardTitle>
          <CardDescription>
            Grant or revoke Zoho CRM access per user. Enabling a seat requires a matching email address
            in Zoho CRM. The system automatically checks all enabled users daily at midnight — if a
            user is removed from Zoho CRM their access is revoked automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-white rounded-lg border text-center">
              <div className="text-2xl font-bold text-gray-800">{users.length}</div>
              <div className="text-sm text-gray-500">Total Users</div>
            </div>
            <div className="p-4 bg-white rounded-lg border text-center">
              <div className="text-2xl font-bold text-blue-600">{enabledCount}</div>
              <div className="text-sm text-gray-500">CRM Access Granted</div>
            </div>
            <div className="p-4 bg-white rounded-lg border text-center">
              <div className="text-2xl font-bold text-gray-400">{users.length - enabledCount}</div>
              <div className="text-sm text-gray-500">No CRM Access</div>
            </div>
          </div>

          {/* Last sync + manual sync */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-white rounded-lg border">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4 text-gray-400" />
              {lastSync ? (
                <span>
                  Last sync: <span className="font-medium">{new Date(lastSync.synced_at).toLocaleString()}</span>
                  {' '}— checked {lastSync.checked ?? 0}, revoked {lastSync.revoked ?? 0}
                </span>
              ) : (
                <span className="text-gray-400">No sync has run yet</span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              disabled={syncing}
              data-testid="crm-security-sync-btn"
              className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Run Sync Now'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Users
          </CardTitle>
          <CardDescription>
            Toggle the blue switch to grant or revoke CRM access. The system verifies the user's
            email against active Zoho CRM users before enabling access.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="crm-security-search"
            />
          </div>

          {loading ? (
            <div className="text-center py-10 text-gray-400">Loading users...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-gray-400">No users found</div>
          ) : (
            <div className="space-y-2">
              {filtered.map(user => {
                const isToggling = toggling === user.id;
                const isExpanded = expandedUser === user.id;

                return (
                  <div
                    key={user.id}
                    className={`border rounded-lg overflow-hidden transition-all ${
                      user.crm_access_enabled ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200 bg-white'
                    }`}
                  >
                    {/* Main row */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      {/* Expand toggle */}
                      <button
                        onClick={() => setExpandedUser(isExpanded ? null : user.id)}
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                        data-testid={`crm-security-expand-${user.id}`}
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>

                      {/* User info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-sm text-gray-900">
                            {user.full_name || user.username}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${ROLE_COLOURS[user.role] || 'bg-gray-100 text-gray-600'}`}>
                            {user.role}
                          </span>
                          {!user.approved && (
                            <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700">
                              Pending
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{user.email || user.username}</div>
                      </div>

                      {/* CRM access status badge */}
                      <div className="flex-shrink-0 hidden sm:flex items-center gap-1">
                        {user.crm_access_enabled ? (
                          <span className="flex items-center gap-1 text-xs text-blue-600 font-medium">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            CRM Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <AlertCircle className="w-3.5 h-3.5" />
                            No Access
                          </span>
                        )}
                      </div>

                      {/* Toggle */}
                      <Switch
                        checked={user.crm_access_enabled || false}
                        onCheckedChange={() => handleToggle(user.id)}
                        disabled={isToggling || user.role === 'super_admin'}
                        data-testid={`crm-security-toggle-${user.id}`}
                        className="data-[state=checked]:bg-blue-600 flex-shrink-0"
                      />
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-1 border-t bg-gray-50 space-y-3">
                        {/* Branches */}
                        {user.branches?.length > 0 && (
                          <div className="flex flex-wrap items-center gap-2">
                            <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-500 font-medium">Branches:</span>
                            {user.branches.map(b => (
                              <Badge key={b} variant="outline" className="text-xs px-2 py-0.5 border-blue-200 text-blue-700">
                                {b}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {/* Groups */}
                        {user.groups?.length > 0 && (
                          <div className="flex flex-wrap items-center gap-2">
                            <Tag className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-500 font-medium">Groups:</span>
                            {user.groups.map(g => (
                              <Badge key={g} variant="outline" className="text-xs px-2 py-0.5 border-gray-200 text-gray-600">
                                {GROUP_LABELS[g] || g}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {user.branches?.length === 0 && user.groups?.length === 0 && (
                          <p className="text-xs text-gray-400 italic">No branch or group assignments</p>
                        )}

                        {user.role === 'super_admin' && (
                          <p className="text-xs text-purple-600 italic">Super admin — CRM access always available</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* How it works */}
      <Card className="border-gray-200 bg-gray-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-gray-700">How CRM Security Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-gray-600">
          <div className="flex gap-2">
            <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
            <p>Click the blue toggle next to a user to grant CRM access.</p>
          </div>
          <div className="flex gap-2">
            <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
            <p>The system checks if the user's email exists in Zoho CRM's active users. If no match is found, access is not granted.</p>
          </div>
          <div className="flex gap-2">
            <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
            <p>Every day at midnight, enabled users are automatically checked against Zoho CRM. If a user has been removed from Zoho, their CRM access is revoked here automatically.</p>
          </div>
          <div className="flex gap-2">
            <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
            <p>You can also run a manual sync at any time using the "Run Sync Now" button above.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CRMSecurity;
